/* Barre d'onglets de variante (spec 13, lot 2) — partagée entre
   exam-import-review.tsx et content-editor.tsx. Un onglet par
   `content_documents.variants`, le premier = variante de référence.
   Bascule instantanée (pas de rechargement de page), renommage inline du
   `label` (jamais la `key`, stable, jamais affichée). */

import { useState } from 'react';
import { cn } from '@/lib/utils';

export interface VariantDeclaration {
  key: string;
  label: string;
}

export default function VariantTabs({
  variants,
  activeKey,
  onSelect,
  onRename,
  className,
}: {
  variants: readonly VariantDeclaration[];
  /** `null` = variante de référence (premier élément de `variants`). */
  activeKey: string | null;
  onSelect: (key: string | null) => void;
  onRename: (key: string, label: string) => void;
  className?: string;
}) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  if (variants.length < 2) return null;

  const commit = (key: string) => {
    setEditingKey(null);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== variants.find((v) => v.key === key)?.label) onRename(key, trimmed);
  };

  return (
    <div className={cn('flex items-center gap-1 flex-wrap', className ?? 'px-5 pt-3')}>
      {variants.map((v, i) => {
        const key = i === 0 ? null : v.key;
        const active = activeKey === key;
        const editing = editingKey === v.key;
        return editing ? (
          <input
            key={v.key}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit(v.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit(v.key);
              if (e.key === 'Escape') setEditingKey(null);
            }}
            className="text-[12.5px] font-bold px-3 py-1.5 rounded-full border-[1.5px] border-naia-blue bg-naia-sky text-naia-blue outline-none"
            style={{ width: `${Math.max(draft.length, 6)}ch` }}
          />
        ) : (
          <button
            key={v.key}
            type="button"
            onClick={() => onSelect(key)}
            onDoubleClick={() => { setEditingKey(v.key); setDraft(v.label); }}
            title="Double-clic pour renommer"
            className={cn(
              'text-[12.5px] font-bold px-3 py-1.5 rounded-full border-[1.5px] transition-colors cursor-pointer',
              active
                ? 'border-naia-blue bg-naia-sky text-naia-blue'
                : 'border-border bg-card text-[var(--naia-text-secondary)] hover:border-naia-blue',
            )}
          >
            {v.label}
            {i === 0 && <span className="opacity-60 font-normal"> · référence</span>}
          </button>
        );
      })}
    </div>
  );
}
